import { apiFetch, storeCsrfToken } from '@/api/apiFetch';
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, AlertCircle, Info } from 'lucide-react';
import { safeStorage } from '@/lib/safeStorage';
import { useAuth } from '@/lib/AuthContext';
import { takeLoginReturn } from '@/lib/loginReturn';
import AuthShell, { labelStyle, inputStyle, iconStyle } from '@/components/auth/AuthShell';

/* ── OAuth Config ── */
const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN || ''; // must be set in .env — no hardcoded fallback
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
const COGNITO_REDIRECT_URI = typeof window !== 'undefined' ? window.location.origin + import.meta.env.BASE_URL + 'login' : '';
// Apple Sign In esta apagado mientras `apple_client_id` siga vacio en Cognito: sin el
// proveedor dado de alta, el boton solo lleva a una pantalla de error de Cognito.
const APPLE_LOGIN_ENABLED = import.meta.env.VITE_ENABLE_APPLE_LOGIN === 'true';

/* ── Google SVG logo ── */
const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 0 0 1 12c0 1.94.46 3.77 1.18 5.07l3.66-2.98z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const AppleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // apiFetch manda aquí con `?reason=idle` cuando el backend cierra la sesión por
  // inactividad (401 SESSION_IDLE_TIMEOUT). Sin este aviso el usuario solo veía
  // el login de golpe y lo leía como un fallo. Se lee de `window.location` y no
  // con `useSearchParams`: los tests del Login mockean react-router-dom entero.
  const [idleNotice] = useState(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('reason') === 'idle',
  );
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();

  /* ── OAuth callback interceptor ── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (code) {
      window.history.replaceState({}, document.title, window.location.pathname);
      exchangeCodeForTokens(code, state);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function exchangeCodeForTokens(code, state) {
    setLoading(true);
    setError('');
    try {
      // `state` es obligatorio en el backend (one-time token en Redis): sin el
      // devuelve 400 y el login social no llega a intercambiar el codigo.
      const result = await apiFetch('/api/auth/oauth-callback', {
        method: 'POST',
        body: JSON.stringify({ code, redirect_uri: COGNITO_REDIRECT_URI, state }),
      });
      if (result.accessToken || result.user) {
        if (result.user) safeStorage.setItem('verix_user', JSON.stringify(result.user));
        if (result.company) safeStorage.setItem('verix_company', JSON.stringify(result.company));
        await refreshAuth();
        navigate(takeLoginReturn({ userId: result.user?.id }) || '/', { replace: true });
      } else {
        setError(t('auth.no_token'));
      }
    } catch (err) {
      setError(err.message || 'Error en autenticación OAuth');
    } finally {
      setLoading(false);
    }
  }

  /* Cognito devuelve el `state` tal cual en la query del callback, y el backend lo
     valida contra Redis. Pedirlo ANTES de redirigir es parte del flujo, no un extra:
     sin el, /api/auth/oauth-callback responde 400. */
  async function startOAuth(provider) {
    setError('');
    let state;
    try {
      // Las rutas de /api/auth/* responden el objeto plano, sin envelope `{ data }`:
      // no pasan por unwrapData/unwrapOne (igual que /login y /me en AuthContext).
      const res = await apiFetch('/api/auth/oauth-state');
      state = res?.state;
    } catch {
      state = null;
    }
    if (!state) {
      setError(t('auth.oauth_state_failed'));
      return;
    }
    window.location.href = `${COGNITO_DOMAIN}/oauth2/authorize?response_type=code&client_id=${COGNITO_CLIENT_ID}`
      + `&redirect_uri=${encodeURIComponent(COGNITO_REDIRECT_URI)}&identity_provider=${provider}`
      + `&scope=email+openid+profile&state=${encodeURIComponent(state)}`;
  }

  function handleGoogleLogin() {
    return startOAuth('Google');
  }

  function handleAppleLogin() {
    return startOAuth('SignInWithApple');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await apiFetch('/api/auth/login', { method: 'POST', body: JSON.stringify({ email: email, password: password }) });
      if (result.accessToken || result.user) {
        if (result.csrfToken) storeCsrfToken(result.csrfToken);
        if (result.user) safeStorage.setItem('verix_user', JSON.stringify(result.user));
        if (result.company) safeStorage.setItem('verix_company', JSON.stringify(result.company));
        await refreshAuth();
        navigate(takeLoginReturn({ userId: result.user?.id }) || '/', { replace: true });
      } else {
        setError(t('auth.no_token'));
      }
    } catch (err) {
      setError(err.message || t('auth.login_error'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell>
      <h2 className="sf-display" style={{ fontSize: 23, fontWeight: 600, color: 'hsl(var(--foreground))', margin: '0 0 4px', letterSpacing: '-0.02em', textAlign: 'center' }}>
        {t('auth.sign_in')}
      </h2>
      <p style={{ fontSize: 14, color: 'hsl(var(--muted-foreground))', margin: '0 0 22px', textAlign: 'center' }}>
        {t('auth.access_workspace')}
      </p>

      <form onSubmit={handleSubmit} aria-label={t('auth.login_form')} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Email */}
        <div>
          <label style={labelStyle}>{t('auth.corporate_email')}</label>
          <div style={{ position: 'relative' }}>
            <Mail style={iconStyle} />
            <input
              type="email" data-testid="login-email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.placeholder_email')} aria-label={t('auth.email')}
              autoComplete="username" inputMode="email" spellCheck={false}
              className="vx-input" style={inputStyle}
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label style={labelStyle}>{t('auth.password')}</label>
          <div style={{ position: 'relative' }}>
            <Lock style={iconStyle} />
            <input
              type={showPassword ? 'text' : 'password'} data-testid="login-password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" aria-label={t('auth.password')}
              autoComplete="current-password" className="vx-input" style={{ ...inputStyle, paddingRight: 48 }}
            />
            <button
              type="button" onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('auth.hide_password') : t('auth.show_password')}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'hsl(var(--muted-foreground))' }}
            >
              {showPassword ? <EyeOff style={{ width: 18, height: 18 }} /> : <Eye style={{ width: 18, height: 18 }} />}
            </button>
          </div>
        </div>

        {/* Remember + Forgot */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'hsl(var(--muted-foreground))', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" style={{ width: 16, height: 16, accentColor: 'hsl(var(--primary))', borderRadius: 4 }} />
            {t('auth.remember_me')}
          </label>
          <Link to="/forgot-password" style={{ fontSize: 13, fontWeight: 500, color: 'hsl(var(--primary))', textDecoration: 'none' }}>
            {t('auth.forgot_password')}
          </Link>
        </div>

        {idleNotice && !error && (
          <div role="status" data-testid="login-idle-notice" style={{ background: 'hsl(var(--primary) / 0.08)', border: '1px solid hsl(var(--primary) / 0.25)', borderRadius: 10, padding: '12px 16px', color: 'hsl(var(--foreground))', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Info style={{ width: 16, height: 16, flexShrink: 0, color: 'hsl(var(--primary))' }} />
            {t('auth.session_idle_timeout')}
          </div>
        )}

        {error && (
          <div style={{ background: 'hsl(var(--destructive) / 0.1)', border: '1px solid hsl(var(--destructive) / 0.3)', borderRadius: 10, padding: '12px 16px', color: 'hsl(var(--destructive))', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
            {error}
          </div>
        )}

        <button
          type="submit" data-testid="login-submit" disabled={loading}
          aria-label={t('auth.sign_in')} className="vx-btn-primary"
          style={{ width: '100%', height: 46, borderRadius: 12, border: 'none', color: 'hsl(var(--primary-foreground))', fontSize: 15, fontWeight: 600, cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: loading ? 0.7 : 1 }}
        >
          {loading ? (
            <><Loader2 style={{ width: 18, height: 18, animation: 'spin 1s linear infinite' }} /> {t('auth.authenticating')}</>
          ) : (
            <>{t('auth.sign_in')} <ArrowRight style={{ width: 16, height: 16 }} /></>
          )}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '2px 0' }}>
          <div style={{ flex: 1, height: 1, background: 'hsl(var(--border))' }} />
          <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', whiteSpace: 'nowrap' }}>{t('auth.or_continue_with')}</span>
          <div style={{ flex: 1, height: 1, background: 'hsl(var(--border))' }} />
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button type="button" onClick={handleGoogleLogin} aria-label={t('auth.sign_in_google')} className="vx-btn-oauth sf-chip" style={{ flex: 1, height: 48, borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 500, color: 'hsl(var(--foreground))' }}>
            <GoogleLogo /> Google
          </button>
          {APPLE_LOGIN_ENABLED && (
            <button type="button" onClick={handleAppleLogin} aria-label={t('auth.sign_in_apple')} className="vx-btn-oauth sf-chip" style={{ flex: 1, height: 48, borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 500, color: 'hsl(var(--foreground))' }}>
              <AppleLogo /> Apple
            </button>
          )}
        </div>

        {/* /register es el alta pública: cualquier visita sin token de invitación
            acaba en POST /auth/register-promo. La landing entra por la misma
            pantalla con `?promo=lanzamiento`, que hoy no cambia nada.
            Si la campaña está cerrada por ventana o kill switch, el 403 lo
            devuelve el backend al enviar: el ERP no puede saberlo antes. */}
        <p style={{ textAlign: 'center', fontSize: 13, color: 'hsl(var(--muted-foreground))', margin: '4px 0 0' }}>
          {t('auth.no_account')}{' '}
          <Link to="/register" data-testid="login-register-link" style={{ fontWeight: 500, color: 'hsl(var(--primary))', textDecoration: 'none' }}>
            {t('auth.create_account')}
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
